import { toBlob } from 'html-to-image';
import { formatImageUrl } from './imageUrl';
import { clientApi } from './clientApi';
import { haptic, tgWebApp } from './telegram';

type TelegramShareBridge = ReturnType<typeof tgWebApp> & {
  openTelegramLink?: (url: string) => void;
  openLink?: (url: string) => void;
  shareMessage?: (msgId: string, callback?: (sent: boolean) => void) => void;
  showAlert?: (message: string) => void;
  /** Available in Telegram clients that support Stories (feature-detected at runtime). */
  shareToStory?: (mediaUrl: string, params?: { text?: string; widget_link?: { url: string; name?: string } }) => void;
};

type FileShareNavigator = Navigator & {
  canShare?: (data: ShareData) => boolean;
};

function notify(message: string) {
  const tg = tgWebApp() as TelegramShareBridge;
  if (tg?.showAlert) tg.showAlert(message);
  else window.alert(message);
}

function copyWithTextarea(text: string): boolean {
  const input = document.createElement('textarea');
  input.value = text;
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  const ok = document.execCommand('copy');
  input.remove();
  return ok;
}

export async function copyText(text: string, successMessage = 'Ссылка скопирована'): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      haptic.success();
      notify(successMessage);
      return true;
    }
  } catch {
    // Fall through to textarea copy.
  }

  if (copyWithTextarea(text)) {
    haptic.success();
    notify(successMessage);
    return true;
  }

  haptic.error();
  notify('Не удалось скопировать ссылку');
  return false;
}

function imageFileName(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const name = pathname.split('/').filter(Boolean).pop();
    if (name?.includes('.')) return name;
  } catch {
    // Keep the default name below.
  }
  return 'fitbot-image.jpg';
}

async function imageFileFromUrl(url: string): Promise<File> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
  return new File([blob], imageFileName(url), { type });
}

function telegramShareUrl(url: string, title: string): string {
  const params = new URLSearchParams({
    url,
    text: title,
  });
  return `https://t.me/share/url?${params.toString()}`;
}

async function sharePreparedTelegramPhoto(
  url: string,
  title: string,
  context?: "consumer",
): Promise<boolean> {
  const tg = tgWebApp() as TelegramShareBridge;
  if (!tg?.shareMessage) return false;

  try {
    const prepared = await clientApi<{ preparedMessageId: string }>('/share/photo', {
      method: 'POST',
      body: JSON.stringify({ imageUrl: url, title, ...(context ? { context } : {}) }),
    });

    await new Promise<void>((resolve, reject) => {
      try {
        tg.shareMessage?.(prepared.preparedMessageId, (sent) => {
          if (sent) haptic.success();
          resolve();
        });
      } catch (error) {
        reject(error);
      }
    });
    return true;
  } catch {
    return false;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('empty_reader_result'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('blob_read_failed'));
    reader.readAsDataURL(blob);
  });
}

async function sharePreparedTelegramTrendCard(imageDataUrl: string, title: string): Promise<boolean> {
  const tg = tgWebApp() as TelegramShareBridge;
  if (!tg?.shareMessage) return false;

  try {
    const prepared = await clientApi<{ preparedMessageId: string }>('/share/trend-card', {
      method: 'POST',
      body: JSON.stringify({ imageDataUrl, title }),
    });

    await new Promise<void>((resolve, reject) => {
      try {
        tg.shareMessage?.(prepared.preparedMessageId, (sent) => {
          if (sent) haptic.success();
          resolve();
        });
      } catch (error) {
        reject(error);
      }
    });
    return true;
  } catch {
    return false;
  }
}

export async function shareElementScreenshot(element: HTMLElement, title: string): Promise<void> {
  let blob: Blob | null = null;
  try {
    blob = await toBlob(element, {
      cacheBust: true,
      backgroundColor: '#ffffff',
      pixelRatio: Math.min(window.devicePixelRatio || 2, 3),
    });
  } catch {
    blob = null;
  }

  if (!blob) {
    haptic.error();
    notify('Не удалось подготовить скриншот карточки');
    return;
  }

  const dataUrl = await blobToDataUrl(blob);
  if (await sharePreparedTelegramTrendCard(dataUrl, title)) {
    return;
  }

  const shareNav = navigator as FileShareNavigator;
  try {
    const file = new File([blob], 'fitbot-trend-card.png', { type: blob.type || 'image/png' });
    const data: ShareData = { title, files: [file] };
    if (shareNav.canShare?.(data)) {
      await navigator.share(data);
      haptic.success();
      return;
    }
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    if (aborted) return;
  }

  haptic.error();
  notify('Не удалось отправить скриншот карточки');
}

export async function shareImageUrl(
  url: string,
  title: string,
  context?: "consumer",
): Promise<void> {
  const shareUrl = formatImageUrl(url);
  if (!shareUrl) {
    haptic.error();
    notify('Не удалось подготовить ссылку на изображение');
    return;
  }

  if (await sharePreparedTelegramPhoto(shareUrl, title, context)) {
    return;
  }

  const shareNav = navigator as FileShareNavigator;

  try {
    const file = await imageFileFromUrl(shareUrl);
    const data: ShareData = { title, files: [file] };
    if (shareNav.canShare?.(data)) {
      await navigator.share(data);
      haptic.success();
      return;
    }
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    if (aborted) return;
  }

  try {
    if (navigator.share) {
      await navigator.share({ title, text: title, url: shareUrl });
      haptic.success();
      return;
    }
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    if (aborted) return;
  }

  const tg = tgWebApp() as TelegramShareBridge;
  try {
    const tgShareUrl = telegramShareUrl(shareUrl, title);
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(tgShareUrl);
      haptic.success();
      return;
    }
  } catch {
    // Fall through to copy.
  }

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareUrl);
      haptic.success();
      notify('Ссылка на изображение скопирована');
      return;
    }
  } catch {
    // Fall through to textarea copy.
  }

  if (copyWithTextarea(shareUrl)) {
    haptic.success();
    notify('Ссылка на изображение скопирована');
    return;
  }

  try {
    if (tg?.openLink) tg.openLink(shareUrl);
    else window.open(shareUrl, '_blank', 'noopener,noreferrer');
    haptic.warning();
  } catch {
    haptic.error();
    notify('Не удалось поделиться изображением');
  }
}

export type ShareImageItem = {
  url: string;
  title?: string;
};

/**
 * Share a try-on video (mp4) to Telegram Stories.
 * The video URL is already a public proxy URL — Telegram fetches it directly.
 * ponystack: вотермарк на видео требует ffmpeg на сервере — mp4 уходит как есть.
 */
export function shareVideoToStory(
  videoUrl: string,
  title?: string,
  options?: { silentFallback?: boolean },
): boolean {
  const tg = tgWebApp() as TelegramShareBridge;
  const mediaUrl = formatImageUrl(videoUrl);
  if (!mediaUrl) {
    if (!options?.silentFallback) {
      haptic.error();
      notify('Не удалось подготовить видео для Stories');
    }
    return false;
  }
  if (typeof tg?.shareToStory === 'function') {
    tg.shareToStory(mediaUrl, {
      text: title ? title.slice(0, 200) : undefined,
    });
    haptic.success();
    return true;
  }
  if (!options?.silentFallback) notify('Обновите Telegram, чтобы делиться видео в Stories');
  return false;
}

/**
 * Share a consumer try-on result to Telegram Stories.
 *
 * 1. Calls POST /share/story-image to get a watermarked, publicly reachable URL.
 * 2. Feature-detects `tg.shareToStory`; if present, calls it — Telegram fetches the media.
 * 3. If the method is absent (older clients), falls back to the standard share flow.
 *
 * Returns true if Stories was invoked, false if the fallback path was taken.
 */
export async function shareToStory(imageUrl: string, title?: string): Promise<boolean> {
  const tg = tgWebApp() as TelegramShareBridge;
  const shareUrl = formatImageUrl(imageUrl);
  if (!shareUrl) {
    haptic.error();
    notify('Не удалось подготовить изображение для Stories');
    return false;
  }

  let mediaUrl: string;
  try {
    const result = await clientApi<{ mediaUrl: string }>('/share/story-image', {
      method: 'POST',
      body: JSON.stringify({ imageUrl: shareUrl, ...(title ? { title } : {}) }),
    });
    mediaUrl = result.mediaUrl;
  } catch {
    // If the endpoint fails, fall back to the regular share so the user is never left stranded.
    await shareImageUrl(shareUrl, title ?? 'FitBot', 'consumer');
    return false;
  }

  if (typeof tg?.shareToStory === 'function') {
    tg.shareToStory(mediaUrl, {
      text: title ? title.slice(0, 200) : undefined,
    });
    haptic.success();
    return true;
  }

  // Graceful degradation: older Telegram clients without shareToStory.
  await shareImageUrl(shareUrl, title ?? 'FitBot', 'consumer');
  return false;
}

export async function shareImageUrls(
  items: ShareImageItem[],
  title: string,
  context?: "consumer",
): Promise<void> {
  const shareItems = items
    .map((item) => ({
      title: item.title?.trim() || title,
      url: formatImageUrl(item.url),
    }))
    .filter((item): item is { title: string; url: string } => Boolean(item.url));

  if (!shareItems.length) {
    haptic.error();
    notify('Не удалось подготовить изображения для отправки');
    return;
  }
  if (shareItems.length === 1) {
    await shareImageUrl(shareItems[0].url, shareItems[0].title, context);
    return;
  }

  const shareNav = navigator as FileShareNavigator;
  try {
    const files = await Promise.all(shareItems.slice(0, 10).map((item) => imageFileFromUrl(item.url)));
    const data: ShareData = { title, files };
    if (shareNav.canShare?.(data)) {
      await navigator.share(data);
      haptic.success();
      return;
    }
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    if (aborted) return;
  }

  const text = shareItems.map((item, index) => `${index + 1}. ${item.title}\n${item.url}`).join('\n\n');
  try {
    if (navigator.share) {
      await navigator.share({ title, text });
      haptic.success();
      return;
    }
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    if (aborted) return;
  }

  await copyText(text, 'Ссылки на изображения скопированы');
}
